import React, { useState, useEffect } from 'react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const SMSOTPAuth = ({ onSuccess, onError, defaultCountryCode = "+965", buttonText = "📱 Mobile Phone Authentication", buttonClass = "w-full flex items-center justify-center px-4 py-3 border border-gray-300 rounded-lg shadow-sm bg-white text-gray-700 hover:bg-gray-50 transition-colors" }) => {
  const [step, setStep] = useState(1); // 1: phone input, 2: OTP input
  const [phoneNumber, setPhoneNumber] = useState(defaultCountryCode);
  const [otp, setOTP] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [countdown, setCountdown] = useState(0);
  const [demoMode, setDemoMode] = useState(false);
  const [demoCode, setDemoCode] = useState('');
  const [showModal, setShowModal] = useState(false);

  // Countdown timer for resend
  useEffect(() => {
    let timer;
    if (countdown > 0) {
      timer = setTimeout(() => setCountdown(countdown - 1), 1000);
    }
    return () => clearTimeout(timer);
  }, [countdown]);

  // Format phone number for Kuwait (+965)
  const formatKuwaitPhone = (input) => {
    // Remove all non-digit characters except +
    let cleaned = input.replace(/[^\d+]/g, '');
    
    // Handle different input formats
    if (cleaned.startsWith('+965')) {
      return cleaned;
    } else if (cleaned.startsWith('965')) {
      return '+' + cleaned;
    } else if (cleaned.startsWith('0') && cleaned.length === 9) {
      // Kuwait numbers starting with 0 (remove 0, add +965)
      return '+965' + cleaned.substring(1);
    } else if (cleaned.length === 8) {
      // 8-digit Kuwait number
      return '+965' + cleaned;
    } else if (cleaned.startsWith('+')) {
      return cleaned;
    }
    
    return input;
  };

  const validateKuwaitPhone = (phone) => {
    // Check if it's a valid Kuwait phone number (+965 followed by 8 digits)
    return /^\+965[0-9]{8}$/.test(phone);
  };

  const sendOTP = async () => {
    if (!phoneNumber.trim()) {
      setError('Please enter your phone number');
      return;
    }

    const formattedPhone = formatKuwaitPhone(phoneNumber);
    
    if (!validateKuwaitPhone(formattedPhone)) {
      setError('Please enter a valid Kuwait phone number (8 digits)');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const response = await axios.post(`${API}/auth/sms/send-otp`, {
        phone_number: formattedPhone
      });

      if (response.data.demo_mode) {
        setDemoMode(true);
        setDemoCode(response.data.demo_code);
      }

      setPhoneNumber(formattedPhone);
      setStep(2);
      setCountdown(60); // 60 seconds before resend
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to send verification code');
    } finally {
      setLoading(false);
    }
  };

  const verifyOTP = async () => {
    if (!otp.trim() || otp.length !== 6) {
      setError('Please enter a valid 6-digit code');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const response = await axios.post(`${API}/auth/sms/verify-otp`, {
        phone_number: phoneNumber,
        verification_code: otp
      });

      if (response.data.valid) {
        if (response.data.session_token) {
          // Existing user login
          localStorage.setItem('session_token', response.data.session_token);
          onSuccess && onSuccess(response.data.user, response.data.session_token);
        } else {
          // New user - handle registration
          onSuccess && onSuccess({ phone: phoneNumber, new_user: true }, null);
        }
      } else {
        setError('Invalid verification code. Please try again.');
      }
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to verify code');
    } finally {
      setLoading(false);
    }
  };

  const resendOTP = async () => {
    if (countdown > 0) return;
    
    setLoading(true);
    setError('');
    setOTP('');

    try {
      await axios.post(`${API}/auth/sms/send-otp`, {
        phone_number: phoneNumber
      });
      
      setCountdown(60);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to resend code');
    } finally {
      setLoading(false);
    }
  };

  const handlePhoneInputChange = (e) => {
    const input = e.target.value;
    setPhoneNumber(input);
    setError('');
  };

  const handleOTPInputChange = (e) => {
    const input = e.target.value.replace(/\D/g, '').slice(0, 6);
    setOTP(input);
    setError('');
  };

  const goBack = () => {
    setStep(1);
    setOTP('');
    setError('');
    setDemoMode(false);
    setDemoCode('');
  };

  if (!showModal) {
    return (
      <button
        onClick={() => setShowModal(true)}
        className={buttonClass}
      >
        {buttonText}
      </button>
    );
  }

  if (step === 1) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
        <div className="max-w-md w-full bg-white rounded-xl shadow-lg p-8">
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-xl font-bold text-gray-900">Phone Verification</h2>
            <button
              onClick={() => setShowModal(false)}
              className="text-gray-400 hover:text-gray-600"
            >
              ✕
            </button>
          </div>
          
          <p className="text-gray-600 mb-6">Enter your Kuwait phone number to continue</p>

          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Kuwait Phone Number
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <span className="text-gray-500 text-sm">🇰🇼 +965</span>
                </div>
                <input
                  type="tel"
                  value={phoneNumber}
                  onChange={handlePhoneInputChange}
                  placeholder="12345678"
                  className="w-full pl-20 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  maxLength="20"
                />
              </div>
              <p className="text-xs text-gray-500 mt-1">
                Enter your 8-digit Kuwait mobile number
              </p>
            </div>

            {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-3">
              <div className="flex">
                <svg className="w-5 h-5 text-red-400 mr-2" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                </svg>
                <span className="text-red-700 text-sm">{error}</span>
              </div>
            </div>
          )}

          <button
            onClick={sendOTP}
            disabled={loading}
            className={`w-full py-3 px-4 rounded-lg font-semibold text-white transition-colors ${
              loading
                ? 'bg-gray-400 cursor-not-allowed'
                : 'bg-blue-600 hover:bg-blue-700'
            }`}
          >
            {loading ? (
              <div className="flex items-center justify-center">
                <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                Sending...
              </div>
            ) : (
              'Send Verification Code'
            )}
          </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="max-w-md w-full bg-white rounded-xl shadow-lg p-8">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-xl font-bold text-gray-900">Enter Verification Code</h2>
          <button
            onClick={() => setShowModal(false)}
            className="text-gray-400 hover:text-gray-600"
          >
            ✕
          </button>
        </div>
        
        <p className="text-gray-600 mb-4">
          We sent a 6-digit code to{' '}
          <span className="font-semibold text-gray-900">{phoneNumber}</span>
        </p>
      </div>

      {demoMode && (
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-4">
          <div className="flex">
            <svg className="w-5 h-5 text-yellow-400 mr-2" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
            </svg>
            <div>
              <p className="text-yellow-700 text-sm font-medium">Demo Mode</p>
              <p className="text-yellow-600 text-sm">Use code: <strong>{demoCode}</strong></p>
            </div>
          </div>
        </div>
      )}

      <div className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Verification Code
          </label>
          <input
            type="text"
            value={otp}
            onChange={handleOTPInputChange}
            placeholder="000000"
            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-center text-2xl font-mono tracking-widest"
            maxLength="6"
            autoComplete="one-time-code"
          />
        </div>

        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-3">
            <div className="flex">
              <svg className="w-5 h-5 text-red-400 mr-2" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
              </svg>
              <span className="text-red-700 text-sm">{error}</span>
            </div>
          </div>
        )}

        <button
          onClick={verifyOTP}
          disabled={loading || otp.length !== 6}
          className={`w-full py-3 px-4 rounded-lg font-semibold text-white transition-colors ${
            loading || otp.length !== 6
              ? 'bg-gray-400 cursor-not-allowed'
              : 'bg-blue-600 hover:bg-blue-700'
          }`}
        >
          {loading ? (
            <div className="flex items-center justify-center">
              <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
              Verifying...
            </div>
          ) : (
            'Verify Code'
          )}
        </button>

        <div className="flex items-center justify-between">
          <button
            onClick={goBack}
            className="text-gray-600 hover:text-gray-800 text-sm font-medium"
          >
            ← Change Phone Number
          </button>

          <button
            onClick={resendOTP}
            disabled={countdown > 0 || loading}
            className={`text-sm font-medium ${
              countdown > 0 || loading
                ? 'text-gray-400 cursor-not-allowed'
                : 'text-blue-600 hover:text-blue-700'
            }`}
          >
            {countdown > 0 ? `Resend in ${countdown}s` : 'Resend Code'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default SMSOTPAuth;